/**
 * Generic undo/redo history manager.
 *
 * Stores deep-cloned snapshots of serializable state.
 * Designed to be used with Zustand — call `push()` before a mutation,
 * then `undo()`/`redo()` to restore previous snapshots.
 *
 * The undo stack uses a fixed-capacity ring buffer so evicting the oldest
 * entry is O(1) instead of O(n) array shift.  The redo stack stays a plain
 * array (it's cleared on every new push and is always small).
 *
 * When constructed with a `sizeOf` function and a `maxBytes` budget, the
 * manager also evicts from the tail until total retained snapshot bytes
 * fall under budget. This prevents scenes with large meshes from exhausting
 * memory on mobile devices — a flat count cap is byte-blind and allows
 * a 1 M-face scene's undo stack to consume gigabytes where a 2 k-face
 * scene's stack would consume megabytes. `minEntries` keeps the most
 * recent N entries regardless of byte budget so undo is never fully
 * blocked, even for extremely large scenes.
 */

import { dlog } from "../debug/debugLogger.ts";

export interface UndoManagerOpts<T> {
  /** Maximum number of entries retained. Default 100. */
  maxEntries?: number;
  /** Maximum total retained bytes across snapshots. Default Infinity. */
  maxBytes?: number;
  /** Minimum entries to retain even when over budget. Default 1. */
  minEntries?: number;
  /** Size estimator for byte-budget eviction. Required when maxBytes is finite. */
  sizeOf?: (entry: T) => number;
}

/**
 * Observer for snapshot-history mutations. Fires after every successful
 * `push()` — i.e. the start of a command that affects undo state. Used by
 * the autosave scheduler to ensure a local backup is scheduled as soon as
 * a command begins, protecting against crashes mid-operator or mid-transform.
 */
export type UndoPushListener = () => void;

interface Slot<T> {
  snap: T;
  bytes: number;
}

const HISTORY_GROWTH_LOG_STEP_BYTES = 20 * 1024 * 1024;

function fmtMiB(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export class UndoManager<T> {
  private buf: Array<Slot<T> | undefined>;
  private head = 0; // index of the next write slot
  private count = 0; // number of live entries
  private totalBytes = 0;
  private redoStack: Slot<T>[] = [];
  private nextGrowthLogBytes = HISTORY_GROWTH_LOG_STEP_BYTES;

  private readonly capacity: number;
  private readonly minEntries: number;
  private maxBytes: number;
  private readonly sizeOf?: (entry: T) => number;
  private pushListener: UndoPushListener | null = null;

  /** Accepts either an options object or (for back-compat) a number meaning maxEntries. */
  constructor(opts: UndoManagerOpts<T> | number = {}) {
    if (typeof opts === "number") opts = { maxEntries: opts };
    this.capacity = opts.maxEntries ?? 100;
    this.minEntries = Math.max(
      1,
      Math.min(opts.minEntries ?? 1, this.capacity),
    );
    this.maxBytes = opts.maxBytes ?? Number.POSITIVE_INFINITY;
    this.sizeOf = opts.sizeOf;
    this.buf = new Array(this.capacity);
  }

  private retainedBytes(): number {
    return this.totalBytes + this.redoBytesUsed;
  }

  private maybeLogRetainedGrowth(): void {
    const retainedBytes = this.retainedBytes();
    if (
      retainedBytes <
      this.nextGrowthLogBytes - HISTORY_GROWTH_LOG_STEP_BYTES
    ) {
      this.nextGrowthLogBytes =
        Math.floor(retainedBytes / HISTORY_GROWTH_LOG_STEP_BYTES) *
          HISTORY_GROWTH_LOG_STEP_BYTES +
        HISTORY_GROWTH_LOG_STEP_BYTES;
      if (this.nextGrowthLogBytes < HISTORY_GROWTH_LOG_STEP_BYTES) {
        this.nextGrowthLogBytes = HISTORY_GROWTH_LOG_STEP_BYTES;
      }
    }
    if (retainedBytes < this.nextGrowthLogBytes) return;

    const thresholdBytes =
      Math.floor(retainedBytes / HISTORY_GROWTH_LOG_STEP_BYTES) *
      HISTORY_GROWTH_LOG_STEP_BYTES;
    dlog.info(
      "js",
      "undo.history",
      `retained history grew past ${fmtMiB(thresholdBytes)}`,
      {
        retainedBytes,
        thresholdBytes,
        undoBytes: this.totalBytes,
        redoBytes: this.redoBytesUsed,
        undoEntries: this.count,
        redoEntries: this.redoStack.length,
        budgetBytes: this.maxBytes,
      },
    );
    this.nextGrowthLogBytes = thresholdBytes + HISTORY_GROWTH_LOG_STEP_BYTES;
  }

  /** Snapshot the current state before a mutation. Clears the redo stack. */
  push(snapshot: T): void {
    const bytes = this.sizeOf ? this.sizeOf(snapshot) : 0;

    // Ring buffer is full: the slot we're about to overwrite holds the
    // oldest entry — deduct its bytes before the overwrite loses that info.
    if (this.count === this.capacity) {
      const evicted = this.buf[this.head];
      if (evicted) this.totalBytes -= evicted.bytes;
    }

    this.buf[this.head] = { snap: snapshot, bytes };
    this.head = (this.head + 1) % this.capacity;
    if (this.count < this.capacity) this.count++;
    this.totalBytes += bytes;

    // Byte-budget eviction from the tail, respecting the minimum retention.
    while (this.count > this.minEntries && this.totalBytes > this.maxBytes) {
      const tail = (this.head - this.count + this.capacity) % this.capacity;
      const evicted = this.buf[tail];
      if (!evicted) break;
      this.buf[tail] = undefined;
      this.totalBytes -= evicted.bytes;
      this.count--;
    }

    // Redo stack is invalidated by a new push. Dropped entries' bytes
    // don't count against the undo budget (redo bytes aren't tracked).
    this.redoStack.length = 0;
    this.maybeLogRetainedGrowth();
    this.pushListener?.();
  }

  /**
   * Subscribe to `push()` events. Replaces any previous listener.
   * Set to `null` to unsubscribe. Exceptions thrown by the listener are
   * swallowed so observer failures never poison undo state.
   */
  setPushListener(listener: UndoPushListener | null): void {
    if (listener == null) {
      this.pushListener = null;
      return;
    }
    this.pushListener = () => {
      try {
        listener();
      } catch {
        /* observer errors must never disrupt undo state */
      }
    };
  }

  /** Restore previous state. Returns the snapshot to apply, or null if empty. */
  undo(current: T): T | null {
    if (this.count === 0) return null;
    const curBytes = this.sizeOf ? this.sizeOf(current) : 0;
    this.redoStack.push({ snap: current, bytes: curBytes });
    this.head = (this.head - 1 + this.capacity) % this.capacity;
    this.count--;
    const slot = this.buf[this.head]!;
    this.buf[this.head] = undefined; // allow GC
    this.totalBytes -= slot.bytes;
    this.maybeLogRetainedGrowth();
    return slot.snap;
  }

  /** Re-apply a previously undone state. Returns the snapshot, or null if empty. */
  redo(current: T): T | null {
    const top = this.redoStack.pop();
    if (!top) return null;
    const curBytes = this.sizeOf ? this.sizeOf(current) : 0;

    // Pushing current onto the undo ring. If full, evict oldest accounting.
    if (this.count === this.capacity) {
      const evicted = this.buf[this.head];
      if (evicted) this.totalBytes -= evicted.bytes;
    }
    this.buf[this.head] = { snap: current, bytes: curBytes };
    this.head = (this.head + 1) % this.capacity;
    if (this.count < this.capacity) this.count++;
    this.totalBytes += curBytes;

    // Redo-originated pushes respect the same byte budget.
    while (this.count > this.minEntries && this.totalBytes > this.maxBytes) {
      const tail = (this.head - this.count + this.capacity) % this.capacity;
      const evicted = this.buf[tail];
      if (!evicted) break;
      this.buf[tail] = undefined;
      this.totalBytes -= evicted.bytes;
      this.count--;
    }
    this.maybeLogRetainedGrowth();
    return top.snap;
  }

  get canUndo(): boolean {
    return this.count > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Read the top of the undo stack without popping. Lets the caller
   *  inspect the next entry's type before deciding how to apply it
   *  (e.g., snapshot vs. self-contained delta with both directions). */
  peekUndo(): T | undefined {
    if (this.count === 0) return undefined;
    const peekIdx = (this.head - 1 + this.capacity) % this.capacity;
    return this.buf[peekIdx]?.snap;
  }

  /** Read the top of the redo stack without popping. */
  peekRedo(): T | undefined {
    return this.redoStack[this.redoStack.length - 1]?.snap;
  }

  /** Observability: current size of the undo history in bytes (0 when no sizeOf). */
  get bytesUsed(): number {
    return this.totalBytes;
  }

  /** Observability: current number of entries in the undo stack. */
  get size(): number {
    return this.count;
  }

  /** Observability: current number of entries in the redo stack. */
  get redoSize(): number {
    return this.redoStack.length;
  }

  /** Observability: estimated retained bytes across redo entries. */
  get redoBytesUsed(): number {
    let total = 0;
    for (const slot of this.redoStack) total += slot.bytes;
    return total;
  }

  /**
   * Update the byte budget at runtime (e.g. when the user changes the
   * history-memory slider). Evicts oldest entries immediately if the new
   * budget is lower than current usage, respecting minEntries.
   */
  setMaxBytes(maxBytes: number): void {
    this.maxBytes = maxBytes;
    while (this.count > this.minEntries && this.totalBytes > this.maxBytes) {
      const tail = (this.head - this.count + this.capacity) % this.capacity;
      const evicted = this.buf[tail];
      if (!evicted) break;
      this.buf[tail] = undefined;
      this.totalBytes -= evicted.bytes;
      this.count--;
    }
    this.maybeLogRetainedGrowth();
  }

  clear(): void {
    this.buf.fill(undefined);
    this.head = 0;
    this.count = 0;
    this.totalBytes = 0;
    this.redoStack.length = 0;
    this.nextGrowthLogBytes = HISTORY_GROWTH_LOG_STEP_BYTES;
  }

  /** Export both stacks for persistence (oldest → newest order). */
  getStacks(): { undoStack: T[]; redoStack: T[] } {
    const out: T[] = [];
    const start = (this.head - this.count + this.capacity) % this.capacity;
    for (let i = 0; i < this.count; i++) {
      const slot = this.buf[(start + i) % this.capacity];
      if (slot) out.push(slot.snap);
    }
    return { undoStack: out, redoStack: this.redoStack.map((s) => s.snap) };
  }

  /** Restore stacks from persisted data. */
  setStacks(undoStack: T[], redoStack: T[]): void {
    this.clear();
    // Only import up to capacity, keeping the newest entries; byte budget
    // is also enforced implicitly via push().
    const start = Math.max(0, undoStack.length - this.capacity);
    for (let i = start; i < undoStack.length; i++) {
      this.push(undoStack[i]);
    }
    // push() clears redoStack, so restore it after.
    this.redoStack = redoStack.map((s) => ({
      snap: s,
      bytes: this.sizeOf ? this.sizeOf(s) : 0,
    }));
    this.maybeLogRetainedGrowth();
  }
}
